import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, Play } from "lucide-react";

const MUTE_KEY = "gc_splash_muted";

export function Preloader() {
    const [phase, setPhase] = useState("show");
    // "on" = playing with sound | "blocked" = browser needs one tap for sound | "muted" = user prefers silence
    const [sound, setSound] = useState("on");
    const videoRef = useRef(null);

    useEffect(() => {
        const v = videoRef.current;
        const userMuted = localStorage.getItem(MUTE_KEY) === "1";
        if (v) {
            if (userMuted) {
                v.muted = true;
                setSound("muted");
                v.play().catch(() => {});
            } else {
                // Default: sound ON from the moment the site opens
                v.muted = false;
                v.play()
                    .then(() => setSound("on"))
                    .catch(() => {
                        // Browser requires one gesture for audio — pause at frame 0 and ask for that tap,
                        // so nobody watches the intro silently.
                        v.pause();
                        v.currentTime = 0;
                        setSound("blocked");
                    });
            }
        }
        // Safety fallback: if the video can't play, still reveal the site.
        const t1 = setTimeout(() => setPhase("fade"), 14000);
        return () => clearTimeout(t1);
    }, []);

    useEffect(() => {
        if (phase !== "fade") return;
        const t2 = setTimeout(() => setPhase("gone"), 750);
        return () => clearTimeout(t2);
    }, [phase]);

    if (phase === "gone") return null;

    const playWithSound = () => {
        const v = videoRef.current;
        if (v) {
            v.currentTime = 0;
            v.muted = false;
            v.volume = 1;
            v.play().catch(() => {});
        }
        setSound("on");
        localStorage.setItem(MUTE_KEY, "0");
    };

    const toggleSound = () => {
        const v = videoRef.current;
        if (sound === "on") {
            if (v) v.muted = true;
            setSound("muted");
            localStorage.setItem(MUTE_KEY, "1");
        } else {
            if (v) {
                v.muted = false;
                v.volume = 1;
                v.play().catch(() => {});
            }
            setSound("on");
            localStorage.setItem(MUTE_KEY, "0");
        }
    };

    return (
        <div
            className={`fixed inset-0 z-[100] bg-navy-deep overflow-hidden transition-opacity duration-700 ${phase === "fade" ? "opacity-0" : "opacity-100"}`}
            data-testid="preloader"
            aria-hidden={phase !== "show"}
        >
            {/* full-screen logo reveal video with its own sound */}
            <video
                ref={videoRef}
                playsInline
                preload="auto"
                onEnded={() => setPhase("fade")}
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="preloader-logo-video"
            >
                <source src="/logo-reveal.webm" type="video/webm" />
                <source src="/logo-reveal.mp4" type="video/mp4" />
            </video>

            {/* one-tap gate when the browser requires a gesture for audio */}
            {sound === "blocked" && phase === "show" && (
                <div className="absolute inset-0 w-full h-full flex flex-col items-center justify-center gap-5 bg-navy-deep/30">
                    <button
                        onClick={playWithSound}
                        data-testid="preloader-play-sound-btn"
                        className="inline-flex items-center gap-3 rounded-full bg-saffron text-white font-display font-black text-sm sm:text-base uppercase tracking-wider px-8 py-4 shadow-2xl animate-pulse"
                    >
                        <Play className="h-5 w-5 fill-white" /> Tap to Play with Sound
                    </button>
                    <button
                        onClick={() => setPhase("fade")}
                        data-testid="preloader-skip-btn"
                        className="text-white/60 hover:text-white text-xs font-bold uppercase tracking-wider underline underline-offset-4 transition-colors"
                    >
                        Skip intro
                    </button>
                </div>
            )}

            {/* sound toggle — remembered across visits */}
            {sound !== "blocked" && phase === "show" && (
                <button
                    onClick={toggleSound}
                    data-testid="preloader-sound-toggle"
                    className="absolute top-5 right-5 inline-flex items-center gap-2 rounded-full bg-white/90 text-navy text-xs font-display font-black uppercase tracking-wider px-4 py-2.5 shadow-lg hover:bg-white transition-colors"
                >
                    {sound === "on" ? (
                        <><VolumeX className="h-4 w-4" /> Tap to Mute</>
                    ) : (
                        <><Volume2 className="h-4 w-4" /> Tap for Sound</>
                    )}
                </button>
            )}
        </div>
    );
}
